import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useSpring, useTransform } from 'motion/react';
import useApi from '../api';
import { conLog } from '../utils/conLog';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

// Customer-facing credit consumption for the current billing cycle
// (GET /usage/credits — whole-number credits and units of agent work only;
// the payload carries no cost basis by construction). Renders nothing until
// the org has metered charges, so pre-metering orgs see no change.

interface CreditMeter {
    meter: string;
    label: string;
    credits: number;
    count: number;
}

interface CreditCategory {
    key: string;
    label: string;
    credits: number;
    meters: CreditMeter[];
}

interface CreditAllotment {
    // null/0 = no recurring allotment (e.g. a free signup grant only): the
    // balance is granted/used/remaining with nothing resetting each cycle.
    monthly_allotment: number | null;
    granted: number; // this cycle's plan grant + unexpired pack remainder
    used: number;
    remaining: number;
    exhausted: boolean;
}

interface CreditUsageData {
    period_start: string;
    period_end: string;
    cycles_back: number;
    earliest_usage_date: string | null;
    credits_used: number;
    allotment: CreditAllotment | null;
    categories: CreditCategory[];
    daily: { date: string; credits: number; meters: Record<string, number> }[];
    metering_active: boolean;
}

// ---------------------------------------------------------------------------
// Segment identities. Color follows the entity, never its rank: each meter
// keeps its slot forever, so the bar reads the same cycle over cycle.
// Adjacent-pair CVD-validated in both modes (dataviz palette, slots 1–7);
// everything unmapped folds into a neutral "Other". Rendered in this order.
// ---------------------------------------------------------------------------

type SegmentDef = {
    key: string;
    label: string;
    meters: string[]; // payload meters folded into this segment
    light: string;
    dark: string;
};

type DailyPoint = { date: string; credits: number; meters: Record<string, number> };

const SEGMENTS: SegmentDef[] = [
    { key: 'chat', label: 'Conversations', meters: ['ai.chat'], light: '#2a78d6', dark: '#3987e5' },
    // Jobs: the LLM meter (ai.run.service before the Owned Jobs rename) plus sandbox time.
    { key: 'jobs', label: 'Jobs', meters: ['ai.run.job', 'ai.run.service', 'sandbox.compute'], light: '#eb6834', dark: '#d95926' },
    { key: 'proactive', label: 'Proactive outreach', meters: ['ai.run.proactive'], light: '#1baf7a', dark: '#199e70' },
    { key: 'research', label: 'Account research', meters: ['ai.run.research'], light: '#eda100', dark: '#c98500' },
    { key: 'summaries', label: 'Visitor summaries', meters: ['ai.run.summary'], light: '#e87ba4', dark: '#d55181' },
    { key: 'voice', label: 'Voice', meters: ['voice.tts', 'voice.stt'], light: '#008300', dark: '#008300' },
    { key: 'identification', label: 'Visitor identification', meters: ['identification.rb2b'], light: '#4a3aa7', dark: '#9085e9' },
];

const OTHER_SEGMENT = { key: 'other', label: 'Other', light: '#9ca3af', dark: '#6b7280' };

type Segment = {
    key: string;
    label: string;
    credits: number;
    share: number; // 0..100 of the cycle total
    color: { light: string; dark: string };
    meters: string[]; // payload meter names in this segment (for the chart filter)
    // e.g. "412 conversations · 185 visitor summaries" — built from the
    // constituent meters so folded segments stay explainable.
    detail: string;
};

// Credits are whole numbers everywhere customer-facing; the server ceils.
const formatCredits = (value: number) =>
    Math.ceil(value).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatChartDate = (value: string) => {
    const date = new Date(`${value}T00:00:00Z`);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const meterDetail = (meters: CreditMeter[]) =>
    meters
        .filter((meter) => meter.count > 0)
        .sort((a, b) => b.credits - a.credits)
        .slice(0, 3)
        .map((meter) => `${meter.count.toLocaleString()} ${meter.label.toLowerCase()}`)
        .join(' · ');

const buildSegments = (usage: CreditUsageData): Segment[] => {
    const allMeters = usage.categories.flatMap((category) => category.meters);
    const byMeter = new Map(allMeters.map((meter) => [meter.meter, meter]));
    // Shares normalize over the segments' own sum, not credits_used: per-meter
    // values are ceiled individually server-side, so their sum can exceed the
    // once-ceiled total and widths would overflow the track.
    const total = allMeters.reduce((sum, meter) => sum + meter.credits, 0) || 1;
    const claimed = new Set<string>();
    const segments: Segment[] = [];

    for (const def of SEGMENTS) {
        const members = def.meters
            .map((name) => byMeter.get(name))
            .filter((meter): meter is CreditMeter => !!meter);
        def.meters.forEach((name) => claimed.add(name));
        const credits = members.reduce((sum, meter) => sum + meter.credits, 0);
        if (credits <= 0) continue;
        segments.push({
            key: def.key,
            label: def.label,
            credits,
            share: (credits / total) * 100,
            color: { light: def.light, dark: def.dark },
            meters: members.map((meter) => meter.meter),
            detail: meterDetail(members),
        });
    }

    const leftovers = allMeters.filter((meter) => !claimed.has(meter.meter));
    const otherCredits = leftovers.reduce((sum, meter) => sum + meter.credits, 0);
    if (otherCredits > 0) {
        segments.push({
            key: OTHER_SEGMENT.key,
            label: OTHER_SEGMENT.label,
            credits: otherCredits,
            share: (otherCredits / total) * 100,
            color: { light: OTHER_SEGMENT.light, dark: OTHER_SEGMENT.dark },
            meters: leftovers.map((meter) => meter.meter),
            detail: meterDetail(leftovers),
        });
    }
    return segments;
};

// The big total, tweened between values instead of snapping.
const AnimatedCredits = ({ value }: { value: number }) => {
    const spring = useSpring(value, { stiffness: 260, damping: 32 });
    useEffect(() => {
        spring.set(value);
    }, [spring, value]);
    const display = useTransform(spring, (current) => formatCredits(Math.max(current, 0)));
    return <motion.span>{display}</motion.span>;
};

const CreditUsage = () => {
    const { get } = useApi();
    const [usage, setUsage] = useState<CreditUsageData | null>(null);
    const [failed, setFailed] = useState(false);
    // 0 = current billing cycle; chevrons walk previous cycles.
    const [cyclesBack, setCyclesBack] = useState(0);
    // Hover previews a segment; click pins it so the chart can be explored
    // day-by-day for that segment. Hover temporarily overrides a pin.
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);
    const [pinnedKey, setPinnedKey] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        get(`/usage/credits?cycles_back=${cyclesBack}`)
            .then((response: any) => {
                if (!cancelled) {
                    setUsage(response.data);
                    setFailed(false);
                    setHoveredKey(null);
                    setPinnedKey(null);
                }
            })
            .catch((error: any) => {
                // Quietly absent on failure — this panel is additive, never
                // worth an error toast on the home screen.
                conLog('credit usage unavailable', error);
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cyclesBack]);

    const segments = useMemo(() => (usage ? buildSegments(usage) : []), [usage]);
    const activeKey = hoveredKey ?? pinnedKey;
    const activeSegment = segments.find((segment) => segment.key === activeKey) || null;

    // Daily series filtered to the active segment (sum of its meters per day).
    const chartData = useMemo(() => {
        const daily: DailyPoint[] = usage?.daily || [];
        if (!activeSegment) return daily;
        return daily.map((day) => ({
            date: day.date,
            credits: activeSegment.meters.reduce(
                (sum, meter) => sum + (day.meters?.[meter] || 0),
                0
            ),
        }));
    }, [usage, activeSegment]);

    const chartConfig = useMemo(
        () =>
            ({
                credits: {
                    label: activeSegment ? activeSegment.label : 'Credits',
                    color: activeSegment ? `var(--seg-${activeSegment.key})` : 'hsl(var(--primary))',
                },
            }) satisfies ChartConfig,
        [activeSegment]
    );

    // Absent only when nothing ever loaded; a failed cycle-nav fetch keeps
    // showing the last good data instead of unmounting the card.
    if (failed && !usage) {
        return null;
    }

    // Initial load: a skeleton in the card's own shape (header, bar, list +
    // chart) so the panel settles into place instead of popping in.
    if (!usage) {
        return (
            <Card className="rounded-lg border p-4">
                <div className="flex items-center justify-between gap-2">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-7 w-48" />
                </div>
                <Skeleton className="mt-3 h-3 w-full rounded-full" />
                <div className="mt-6 flex flex-col gap-6 lg:flex-row">
                    <div className="w-full space-y-3 lg:w-2/5">
                        {[0, 1, 2].map((row) => (
                            <div key={row} className="flex items-center gap-2.5">
                                <Skeleton className="h-2.5 w-2.5 shrink-0 rounded-full" />
                                <Skeleton className="h-4 flex-1" />
                                <Skeleton className="h-4 w-12 shrink-0" />
                            </div>
                        ))}
                    </div>
                    <Skeleton className="h-[180px] min-w-0 flex-1" />
                </div>
            </Card>
        );
    }

    // All display decisions key off the LOADED payload's cycle, not the
    // requested one — a failed cycle-nav fetch keeps the previous payload, and
    // pairing it with the newer cyclesBack would mislabel the shown period.
    // Chevron clicks navigate relative to what's displayed, which also makes
    // a failed hop retryable.
    const displayCycle = usage.cycles_back ?? 0;

    // Hide entirely only for orgs with no metering at all; keep the card (and
    // its navigation) when browsing an empty past cycle.
    if (!usage.metering_active && displayCycle === 0) {
        return null;
    }

    const allotment = usage.allotment;
    // With an allotment the bar is a meter: segments fill used/granted of the
    // track; without one they fill the full width (usage-only view).
    const fillFraction = allotment && allotment.granted > 0
        ? Math.min(allotment.used / allotment.granted, 1)
        : 1;
    // Backend datetimes are naive UTC; parse the DATE PART with an explicit Z
    // so labels and comparisons never shift with the viewer's timezone
    // (naive ISO parses as local, date-only as UTC — mixing them is off-by-one
    // west of UTC and mislabels east of it).
    const formatPeriodDate = (value: string) => {
        const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
    };
    const cycleResetLabel = formatPeriodDate(usage.period_end) || null;
    const periodLabel = `${formatPeriodDate(usage.period_start)} – ${formatPeriodDate(usage.period_end)}`;
    const canGoBack =
        displayCycle < 24 &&
        !!usage.earliest_usage_date &&
        // ISO date strings compare correctly lexicographically.
        usage.period_start.slice(0, 10) > usage.earliest_usage_date.slice(0, 10);

    return (
        <Card className="credit-usage-viz rounded-lg border p-4">
            {/* Segment colors live on CSS vars so the .dark class swaps the
                whole set (selected dark steps, not an automatic flip). */}
            <style>{`
                .credit-usage-viz { ${segments.map((s) => `--seg-${s.key}: ${s.color.light};`).join(' ')} }
                .dark .credit-usage-viz { ${segments.map((s) => `--seg-${s.key}: ${s.color.dark};`).join(' ')} }
            `}</style>

            {/* Section 1: total + segmented usage bar */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2.5">
                    <Zap className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="text-sm font-medium text-muted-foreground">Agent usage</div>
                    {/* Billing-cycle navigation */}
                    <div className="ml-2 flex items-center gap-0.5 text-xs text-muted-foreground">
                        <button
                            type="button"
                            aria-label="Previous billing cycle"
                            className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                            disabled={!canGoBack}
                            onClick={() => setCyclesBack(displayCycle + 1)}
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <span className="whitespace-nowrap tabular-nums">{periodLabel}</span>
                        <button
                            type="button"
                            aria-label="Next billing cycle"
                            className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                            disabled={displayCycle === 0}
                            onClick={() => setCyclesBack(Math.max(0, displayCycle - 1))}
                        >
                            <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-semibold tracking-tight tabular-nums">
                        <AnimatedCredits value={activeSegment ? activeSegment.credits : usage.credits_used} />
                    </span>
                    {!activeSegment && allotment && (
                        <span className="text-sm text-muted-foreground tabular-nums">
                            / {allotment.granted.toLocaleString()}
                        </span>
                    )}
                    <span className="text-sm text-muted-foreground">
                        {activeSegment
                            ? `credits · ${activeSegment.label.toLowerCase()}`
                            : displayCycle === 0
                            ? 'credits this cycle'
                            : 'credits'}
                    </span>
                </div>
            </div>

            <TooltipProvider delayDuration={100}>
                <div
                    className={`mt-3 flex h-4 w-full items-center gap-[2px] ${
                        allotment ? 'rounded-full bg-muted/70 pr-0' : ''
                    }`}
                    onMouseLeave={() => setHoveredKey(null)}
                >
                    {segments.map((segment) => (
                        <Tooltip key={segment.key}>
                            <TooltipTrigger asChild>
                                <div
                                    onMouseEnter={() => setHoveredKey(segment.key)}
                                    onClick={() =>
                                        setPinnedKey((current) =>
                                            current === segment.key ? null : segment.key
                                        )
                                    }
                                    className={`h-3 min-w-[6px] cursor-pointer rounded-[3px] transition-all duration-200 ease-out first:rounded-l-full last:rounded-r-full ${
                                        activeKey && activeKey !== segment.key
                                            ? 'opacity-50'
                                            : 'opacity-100'
                                    } ${activeKey === segment.key ? 'scale-y-[1.3]' : ''}`}
                                    style={{
                                        width: `${segment.share * fillFraction}%`,
                                        backgroundColor: `var(--seg-${segment.key})`,
                                    }}
                                />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-64">
                                <div className="text-xs font-medium">
                                    {segment.label} · {formatCredits(segment.credits)} credits (
                                    {Math.round(segment.share)}%)
                                </div>
                                {segment.detail && (
                                    <div className="mt-0.5 text-xs text-muted-foreground">{segment.detail}</div>
                                )}
                                <div className="mt-0.5 text-xs text-muted-foreground/70">
                                    {pinnedKey === segment.key ? 'Click to unpin' : 'Click to pin'}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </TooltipProvider>

            {allotment && (
                <div
                    className={`mt-1.5 text-right text-xs tabular-nums ${
                        allotment.exhausted ? 'font-medium text-destructive' : 'text-muted-foreground'
                    }`}
                >
                    {displayCycle === 0 ? (
                        <>
                            {allotment.exhausted
                                ? 'Out of included credits'
                                : `${allotment.remaining.toLocaleString()} remaining`}
                            {cycleResetLabel && !!allotment.monthly_allotment && (
                                <span className="text-muted-foreground/70"> · resets {cycleResetLabel}</span>
                            )}
                        </>
                    ) : allotment.exhausted ? (
                        'Used all included credits'
                    ) : (
                        `${allotment.remaining.toLocaleString()} unused`
                    )}
                </div>
            )}
            {segments.length === 0 && (
                <div className="mt-2 text-sm text-muted-foreground">No usage in this cycle</div>
            )}

            {/* Section 2: breakdown list + daily chart. Hovering a bar
                segment filters the list to that segment's row. */}
            <div className="mt-4 flex flex-col gap-6 lg:flex-row">
                <div className="w-full lg:w-2/5">
                    <AnimatePresence initial={false}>
                        {segments
                            .filter((segment) => !activeKey || segment.key === activeKey)
                            .map((segment) => (
                                <motion.div
                                    key={segment.key}
                                    layout
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.18, ease: 'easeOut' }}
                                    className="overflow-hidden"
                                >
                                    <div className="flex items-center justify-between gap-3 py-[5px]">
                                        <div className="flex min-w-0 items-center gap-2.5">
                                            <span
                                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                style={{ backgroundColor: `var(--seg-${segment.key})` }}
                                            />
                                            <div className="min-w-0">
                                                <div className="truncate text-sm">{segment.label}</div>
                                                {segment.detail && (
                                                    <div className="truncate text-xs text-muted-foreground/70">
                                                        {segment.detail}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                                            {formatCredits(segment.credits)}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                    </AnimatePresence>
                </div>

                <div className="min-w-0 flex-1">
                    {usage.daily.length > 1 ? (
                        <ChartContainer config={chartConfig} className="aspect-auto h-[180px] w-full lg:h-full lg:min-h-[180px]">
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="fillCredits" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--color-credits)" stopOpacity={0.6} />
                                        <stop offset="95%" stopColor="var(--color-credits)" stopOpacity={0.05} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickLine={false}
                                    axisLine={false}
                                    tickMargin={8}
                                    minTickGap={32}
                                    tickFormatter={formatChartDate}
                                />
                                <YAxis tickLine={false} axisLine={false} width={40} />
                                <ChartTooltip
                                    cursor={false}
                                    content={
                                        <ChartTooltipContent
                                            labelFormatter={(value) => formatChartDate(String(value))}
                                            indicator="dot"
                                        />
                                    }
                                />
                                <Area
                                    dataKey="credits"
                                    type="monotone"
                                    fill="url(#fillCredits)"
                                    stroke="var(--color-credits)"
                                    name="credits"
                                />
                            </AreaChart>
                        </ChartContainer>
                    ) : (
                        <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground lg:h-full">
                            Daily usage appears here as your agent works
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default CreditUsage;
